import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { Button, DrawerBody, DrawerFooter, IconButton, Text, useRebrand } from '@growtherapy/sprout-ui';

import {
  CSSRSType,
  MeasureConnected,
  MeasureProps,
  MeasureSchemaType,
  useGetMeasureSchemaByType,
  useShouldPromptForCSSRS,
} from '@grow-therapy-team/seedling-components';

import { DrawerHeader } from '../../components/DrawerHeader';
import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import {
  ProviderDrawerState,
  drawerStateAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData,
  selectedClientUserShortIdAtom,
} from '../state';
import { useGetMeasuresByPatientShortId } from '../useGetMeasuresByPatientShortId';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { useSubmitCSSRSMeasureCallback } from './useSubmitCSSRSMeasureCallback';
import { getCSSRSSubmissionSourceType } from './utils';

type CSSRSFormProps = {
  onClickBackButton: () => void;
  onSubmit: () => Promise<void>;
  connectedMeasureComponent: React.ReactNode;
  isLoading?: boolean;
};

const CSSRSFormHeader = ({ onClickBackButton }: { onClickBackButton: () => void }) => {
  return (
    <DrawerHeader>
      <div className='flex items-center'>
        <IconButton
          onClick={onClickBackButton}
          className='mr-4 w-8 h-8 flex justify-center items-center'
          iconSize='xs'
          aria-label='Go back'
          iconDefinition={faArrowLeft}
        />
        <Text variant='lg' className='rebrand:text-text-inverse'>
          C-SSRS – Suicide Risk
        </Text>
      </div>
    </DrawerHeader>
  );
};

export const CSSRSForm = ({ onClickBackButton, onSubmit, connectedMeasureComponent, isLoading }: CSSRSFormProps) => {
  const isRebrand = useRebrand();

  return (
    <>
      <CSSRSFormHeader onClickBackButton={onClickBackButton} />
      <DrawerBody>{connectedMeasureComponent}</DrawerBody>
      <DrawerFooter>
        <Button loading={isLoading} onClick={onSubmit} className='!ml-auto' use={isRebrand ? 'secondary' : 'primary'}>
          Submit
        </Button>
      </DrawerFooter>
    </>
  );
};

export const CSSRSFormWrapper = () => {
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom);
  const clientInfo = useAtomValue(selectedClientInfoAtom);
  const scheduledPatientInfo = useAtomValue(scheduledPatientInformationAtom);
  const measureBundles = useAtomValue(selectedClientMeasuresData);
  const patientShortId = clientInfo?.patientShortId ?? '';
  const appointmentShortId = clientInfo?.inSessionAppointmentShortId ?? scheduledPatientInfo?.appointmentShortId;

  useGetMeasuresByPatientShortId(patientShortId);
  const { loading: cssrsPromptLoading, result: cssrsPromptResult } = useShouldPromptForCSSRS({
    patientShortId,
    measureBundles,
    appointmentShortId,
  });

  const measureSchemaType =
    cssrsPromptResult.cssrsType === CSSRSType.Child
      ? MeasureSchemaType.COLUMBIA_SUICIDE_SEVERITY_RATING_SCALE_CHILD
      : MeasureSchemaType.COLUMBIA_SUICIDE_SEVERITY_RATING_SCALE;

  const { data: cssrsSchemaData } = useGetMeasureSchemaByType(measureSchemaType);
  const { loading: isLoadingPatientData } = useGetPatientByShortId(patientShortId);
  const [handleSubmitMeasure, { isLoading }] = useSubmitCSSRSMeasureCallback(
    measureSchemaType,
    cssrsPromptResult.previousResponseShortId,
    getCSSRSSubmissionSourceType(cssrsPromptResult),
  );

  const setDrawerState = useSetAtom(drawerStateAtom);
  const onClickBackButton = () => {
    setDrawerState(ProviderDrawerState.MEASURE_RESPONSES);
  };

  if (cssrsPromptLoading || !cssrsSchemaData?.measureSchema || (!clientUserShortId && !isLoadingPatientData)) {
    return;
  }

  return (
    <CSSRSForm
      connectedMeasureComponent={
        <MeasureConnected isInverse schema={cssrsSchemaData.measureSchema as MeasureProps['schema']} />
      }
      onClickBackButton={onClickBackButton}
      onSubmit={handleSubmitMeasure}
      isLoading={isLoading || isLoadingPatientData}
    />
  );
};
